import { Icon } from '@iconify/react'
import type { Product } from '@/types'
import { useCart } from '@/providers/CartProvider'

interface ProductCardProps {
  product: Product
  highlighted?: boolean
}

export function ProductCard({ product, highlighted = false }: ProductCardProps) {
  const { addItem } = useCart()

  return (
    <div
      className={`${
        highlighted
          ? 'bg-navy border border-slate-800 shadow-2xl'
          : 'bg-white border border-slate-200 hover:border-navy/20'
      } rounded-[2rem] p-8 flex flex-col relative transition-colors`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-rust text-white text-xs font-medium px-4 py-1 rounded-full">
          Required
        </div>
      )}
      {product.image && (
        <div className="-mx-8 -mt-8 mb-6 aspect-[4/3] overflow-hidden rounded-t-[2rem]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      {product.step && (
        <div className="text-[10px] font-medium tracking-widest text-slate-400 uppercase mb-2">
          {product.step}
        </div>
      )}
      <h3 className={`text-2xl font-medium tracking-tight mb-2 ${highlighted ? 'text-white' : 'text-navy'}`}>
        {product.name}
      </h3>
      <div className={`text-3xl font-medium mb-6 ${highlighted ? 'text-white' : 'text-rust'}`}>
        ${product.price}{' '}
        <span className={`text-sm font-light ${highlighted ? 'text-white/50' : 'text-slate-400'}`}>
          {product.type === 'recurring' ? '/month' : 'One-time'}
        </span>
      </div>

      <div className="flex-1">
        <p className={`text-sm font-light mb-6 ${highlighted ? 'text-white/70' : 'text-slate-600'}`}>
          {product.description}
        </p>
        <ul className="space-y-3 mb-8">
          {product.features.map((feature) => (
            <li
              key={feature}
              className={`flex items-center gap-2 text-sm font-light ${highlighted ? 'text-white' : 'text-slate-700'}`}
            >
              <Icon icon="solar:check-circle-linear" className="text-rust" />
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={() => addItem(product.id)}
        className={`w-full py-3 rounded-full font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
          highlighted
            ? 'bg-rust text-white hover:bg-rust-dark shadow-lg shadow-rust/20'
            : 'bg-slate-50 text-navy border border-slate-200 hover:bg-slate-100'
        }`}
      >
        Add to Cart <Icon icon="solar:cart-plus-linear" />
      </button>
    </div>
  )
}
